<div class="p-6 space-y-6">

    <!-- HEADER -->
    <div class="flex items-center justify-between">
        <div>
            <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Configuración del Sistema</h1>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Parámetros generales de empresa, tributación y configuración SUNAT.</p>
        </div>
        <div class="flex gap-3">
            <button
                type="button"
                (click)="reset()"
                [disabled]="!hasDirty || saving"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-white/5 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed">
                Cancelar
            </button>
            <button
                type="button"
                (click)="saveAll()"
                [disabled]="!hasDirty || saving"
                class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">
                {{ saving ? 'Guardando...' : 'Guardar cambios' }}
            </button>
        </div>
    </div>

    @if (loading) {
    <div class="text-center py-12 text-gray-500 dark:text-gray-400">Cargando configuración...</div>
    } @else {

    <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">

        <!-- FORMULARIO -->
        <div class="xl:col-span-2 space-y-6">
            @for (group of groups; track group.label) {
            <div class="bg-white dark:bg-white/3 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                <h2 class="text-base font-semibold text-gray-800 dark:text-white mb-1">{{ group.label }}</h2>
                <p class="text-xs text-gray-400 dark:text-gray-500 mb-5 border-b border-gray-100 dark:border-gray-800 pb-4">
                    Completa los campos y guarda los cambios.
                </p>

                <div class="grid grid-cols-2 gap-5">
                    @for (c of group.configs; track c.id) {
                    <div [class]="(c.colSpan === 1 || !c.colSpan) ? 'col-span-2' : 'col-span-1'">
                        <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                            {{ c.description || c.configKey }}
                        </label>

                        @if (isTextarea(c)) {
                        <textarea
                            [(ngModel)]="c.editValue"
                            rows="3"
                            class="w-full px-3 py-2 text-sm text-gray-800 dark:text-white bg-white dark:bg-white/5 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none">
                        </textarea>
                        } @else if (isModeSelect(c)) {
                        <select
                            [(ngModel)]="c.editValue"
                            class="w-full px-3 py-2 text-sm text-gray-800 dark:text-white bg-white dark:bg-white/5 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="ONLINE">ONLINE (inmediato)</option>
                            <option value="OFFLINE">OFFLINE (programado)</option>
                        </select>
                        } @else {
                        <div class="flex items-center gap-2">
                            @if (c.configDatatype === 'DECIMAL' || c.configDatatype === 'INTEGER') {
                            <span class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/3 border border-gray-300 dark:border-gray-700 rounded-lg">
                                {{ (c.configKey.includes('rate') || c.configKey.includes('retencion_rate')) ? '' : 'S/' }}
                            </span>
                            }
                            <input
                                [type]="inputType(c.configDatatype)"
                                [(ngModel)]="c.editValue"
                                [step]="c.configDatatype === 'DECIMAL' ? '0.01' : '1'"
                                class="flex-1 px-3 py-2 text-sm text-gray-800 dark:text-white bg-white dark:bg-white/5 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            @if (c.configKey.includes('rate') || c.configKey.includes('retencion_rate')) {
                            <span class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/3 border border-gray-300 dark:border-gray-700 rounded-lg">%</span>
                            }
                            @if (c.configKey.includes('intervalo')) {
                            <span class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/3 border border-gray-300 dark:border-gray-700 rounded-lg">min</span>
                            }
                        </div>
                        }

                        @if (c.editValue !== c.configValue) {
                        <p class="mt-1 text-xs text-amber-500">Valor modificado — recuerda guardar.</p>
                        }
                    </div>
                    }
                </div>
            </div>
            }

            <!-- TIPO DE CAMBIO -->
            <div class="bg-white dark:bg-white/3 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">

                <h2 class="text-sm font-bold tracking-wide text-gray-700 dark:text-gray-300 uppercase mb-5">
                    Actualización de tipo de cambio
                </h2>

                <!-- Actualizar hoy -->
                <div class="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-white/3 border border-gray-100 dark:border-gray-800 mb-4">
                    <div>
                        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Tipo de cambio de hoy</p>
                        @if (todayRate) {
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Compra: <span class="font-semibold text-gray-700 dark:text-gray-200">S/ {{ todayRate.purchase | number:'1.3-3' }}</span>
                            &nbsp;·&nbsp;
                            Venta: <span class="font-semibold text-gray-700 dark:text-gray-200">S/ {{ todayRate.sale | number:'1.3-3' }}</span>
                        </p>
                        } @else {
                        <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Consulta y registra el TC del día actual desde SBS.</p>
                        }
                    </div>
                    <button type="button" (click)="updateTodayRate()" [disabled]="tcTodayLoading"
                        class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        @if (tcTodayLoading) {
                        <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Actualizando…
                        } @else {
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Actualizar hoy
                        }
                    </button>
                </div>

                <!-- Actualizar por rango -->
                <!-- <div class="p-4 rounded-xl bg-gray-50 dark:bg-white/3 border border-gray-100 dark:border-gray-800">
                    <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Actualizar por rango de fechas</p>
                    <p class="text-xs text-gray-400 dark:text-gray-500 mb-4">El rango debe estar dentro del mismo mes y año.</p>

                    <div class="grid grid-cols-2 gap-4 mb-3">
                        <div>
                            <app-date-picker id="tc-range-from" label="Desde"
                                [defaultDate]="tcRangeFrom || undefined"
                                (dateChange)="onTcFromChange($event)">
                            </app-date-picker>
                        </div>
                        <div>
                            <app-date-picker id="tc-range-to" label="Hasta"
                                [defaultDate]="tcRangeTo || undefined"
                                (dateChange)="onTcToChange($event)">
                            </app-date-picker>
                        </div>
                    </div>

                    @if (tcRangeSubmitted && tcRangeError) {
                    <p class="mb-3 text-xs text-error-500">{{ tcRangeError }}</p>
                    }

                    <div class="flex justify-end">
                        <button type="button" (click)="updateRangeRate()" [disabled]="tcRangeLoading"
                            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                            @if (tcRangeLoading) {
                            <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Actualizando…
                            } @else {
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Actualizar rango
                            }
                        </button>
                    </div>
                </div> -->

            </div>

            <!-- BOTONES INFERIORES -->
            <div class="flex justify-end gap-3 pt-2">
                <button
                    type="button"
                    (click)="reset()"
                    [disabled]="!hasDirty || saving"
                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-white/5 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed">
                    Restablecer
                </button>
                <button
                    type="button"
                    (click)="saveAll()"
                    [disabled]="!hasDirty || saving"
                    class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">
                    {{ saving ? 'Guardando...' : 'Guardar configuración' }}
                </button>
            </div>
        </div>

        <!-- PANEL LATERAL -->
        <div class="space-y-4">
            <div class="bg-white dark:bg-white/3 rounded-2xl border border-gray-200 dark:border-gray-800 p-5">
                <h3 class="text-sm font-semibold text-gray-700 dark:text-white mb-3">Estado y ayuda</h3>
                <p class="text-xs text-gray-400 dark:text-gray-500 mb-4">Resumen rápido de la configuración actual del sistema.</p>

                <div class="space-y-3">
                    <div class="p-3 rounded-lg bg-gray-50 dark:bg-white/3 border border-gray-100 dark:border-gray-800">
                        <p class="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Datos del emisor</p>
                        <p class="text-xs text-gray-400">Se usan en la cabecera de facturas, boletas, notas de crédito, notas de débito y guías de remisión.</p>
                    </div>
                    <div class="p-3 rounded-lg bg-gray-50 dark:bg-white/3 border border-gray-100 dark:border-gray-800">
                        <p class="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Retención</p>
                        <p class="text-xs text-gray-400">La tasa debe mantenerse parametrizada para permitir cambios normativos sin tocar código fuente.</p>
                    </div>
                    <div class="p-3 rounded-lg bg-gray-50 dark:bg-white/3 border border-gray-100 dark:border-gray-800">
                        <p class="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">SUNAT</p>
                        <p class="text-xs text-gray-400">Los modos ONLINE y OFFLINE determinan si los comprobantes se envían al momento de registrar o en lotes programados.</p>
                    </div>
                </div>
            </div>
        </div>

    </div>
    }

</div>
